import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';

// Initial state
const initialState = {
  settings: {
    theme: 'light',
    temperatureUnit: 'C',
    notificationsEnabled: true,
    autoRefreshInterval: 5,
    defaultZoneId: '',
    language: 'en'
  },
  loading: false,
  error: null,
  notification: null
};

// Action types
const SETTINGS_ACTIONS = {
  FETCH_START: 'FETCH_START',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_ERROR: 'FETCH_ERROR',
  UPDATE_SETTINGS: 'UPDATE_SETTINGS',
  SET_NOTIFICATION: 'SET_NOTIFICATION',
  CLEAR_NOTIFICATION: 'CLEAR_NOTIFICATION'
};

// Reducer function
const settingsReducer = (state, action) => {
  switch (action.type) {
    case SETTINGS_ACTIONS.FETCH_START:
      return {
        ...state,
        loading: true,
        error: null
      };
    case SETTINGS_ACTIONS.FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        settings: action.payload,
        error: null
      };
    case SETTINGS_ACTIONS.FETCH_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    case SETTINGS_ACTIONS.UPDATE_SETTINGS:
      return {
        ...state,
        settings: {
          ...state.settings,
          ...action.payload
        }
      };
    case SETTINGS_ACTIONS.SET_NOTIFICATION:
      return {
        ...state,
        notification: {
          type: action.payload.type,
          message: action.payload.message
        }
      };
    case SETTINGS_ACTIONS.CLEAR_NOTIFICATION:
      return {
        ...state,
        notification: null
      };
    default:
      return state;
  }
};

// Create context
const SettingsContext = createContext();

/**
 * Settings Context Provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const SettingsContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(settingsReducer, initialState);

  // Load settings from localStorage on mount
  useEffect(() => {
    const loadSettings = () => {
      dispatch({ type: SETTINGS_ACTIONS.FETCH_START });
      
      try {
        const savedSettings = localStorage.getItem('strawberry-farm-settings');
        
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          
          dispatch({
            type: SETTINGS_ACTIONS.FETCH_SUCCESS,
            payload: parsedSettings
          });
        } else {
          // If no saved settings, use defaults
          dispatch({
            type: SETTINGS_ACTIONS.FETCH_SUCCESS,
            payload: initialState.settings
          });
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        
        dispatch({
          type: SETTINGS_ACTIONS.FETCH_ERROR,
          payload: 'Failed to load settings'
        });
      }
    };
    
    loadSettings();
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('strawberry-farm-settings', JSON.stringify(state.settings));
  }, [state.settings]);

  // Set notification
  const setNotification = useCallback((type, message, duration = 5000) => {
    dispatch({
      type: SETTINGS_ACTIONS.SET_NOTIFICATION,
      payload: { type, message }
    });

    // Auto-clear notification after duration
    setTimeout(() => {
      dispatch({ type: SETTINGS_ACTIONS.CLEAR_NOTIFICATION });
    }, duration);
  }, []);

  // Clear notification
  const clearNotification = useCallback(() => {
    dispatch({ type: SETTINGS_ACTIONS.CLEAR_NOTIFICATION });
  }, []);

  // Update settings
  const updateSettings = useCallback((newSettings) => {
    dispatch({
      type: SETTINGS_ACTIONS.UPDATE_SETTINGS,
      payload: newSettings
    });
    
    setNotification('success', 'Settings updated successfully');
    return true;
  }, [setNotification]);

  // Context value
  const contextValue = {
    state,
    updateSettings,
    setNotification,
    clearNotification
  };

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};

// Custom hook for using the settings context
export const useSettings = () => {
  const context = useContext(SettingsContext);
  
  if (!context) {
    throw new Error('useSettings must be used within a SettingsContextProvider');
  }
  
  return context;
};

export default SettingsContext;
